import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { Search } from 'lucide-react'
import { usePlatformAdminTenants } from '@/hooks/usePlatformAdmin'

const PLAN_COLORS: Record<string, string> = {
  free:       'bg-gray-100 text-gray-600',
  starter:    'bg-blue-100 text-blue-700',
  pro:        'bg-violet-100 text-violet-700',
  enterprise: 'bg-amber-100 text-amber-700',
}

const STATUS_COLORS: Record<string, string> = {
  active:      'bg-green-100 text-green-700',
  trialing:    'bg-blue-100 text-blue-700',
  past_due:    'bg-amber-100 text-amber-700',
  canceled:    'bg-red-100 text-red-700',
  incomplete:  'bg-gray-100 text-gray-600',
}

export function PlatformAdminPage() {
  const { data: tenants, isLoading, error } = usePlatformAdminTenants()
  const [search, setSearch] = useState('')

  const filtered = useMemo(() => {
    if (!tenants) return []
    const q = search.trim().toLowerCase()
    if (!q) return tenants
    return tenants.filter((t) =>
      t.name.toLowerCase().includes(q) ||
      t.slug.toLowerCase().includes(q) ||
      (t.owner_email ?? '').toLowerCase().includes(q)
    )
  }, [tenants, search])

  return (
    <div>
      <div className="mb-6 flex items-end justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Tenants</h1>
          <p className="mt-1 text-sm text-gray-500">
            {isLoading ? 'Loading…' : `${filtered.length} of ${tenants?.length ?? 0} tenants`}
          </p>
        </div>
        <div className="relative">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            aria-label="Search tenants"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name, slug, or owner email…"
            className="w-80 rounded-lg border border-gray-300 py-2 pl-9 pr-3 text-sm focus:border-violet-500 focus:outline-none focus:ring-1 focus:ring-violet-500"
          />
        </div>
      </div>

      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
          Failed to load tenants: {error instanceof Error ? error.message : String(error)}
        </div>
      )}

      {isLoading && (
        <div className="flex justify-center py-16">
          <div className="h-6 w-6 animate-spin rounded-full border-2 border-violet-600 border-t-transparent" />
        </div>
      )}

      {!isLoading && !error && (
        <div className="overflow-hidden rounded-xl border border-gray-200 bg-white">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-100 bg-gray-50 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
                <th className="px-4 py-3">Tenant</th>
                <th className="px-4 py-3">Plan</th>
                <th className="px-4 py-3">Sites</th>
                <th className="px-4 py-3">Members</th>
                <th className="px-4 py-3">Owner</th>
                <th className="px-4 py-3">Subscription</th>
                <th className="px-4 py-3">Created</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filtered.map((t) => (
                <tr key={t.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3">
                    <Link to={`/platform-admin/tenants/${t.id}`} className="font-medium text-violet-700 hover:underline">
                      {t.name}
                    </Link>
                    <p className="text-xs text-gray-400">{t.slug}</p>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${PLAN_COLORS[t.plan] ?? PLAN_COLORS['free']}`}>
                      {t.plan}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-gray-700">{t.site_count}</td>
                  <td className="px-4 py-3 text-gray-700">{t.member_count}</td>
                  <td className="px-4 py-3 text-gray-500">{t.owner_email ?? '—'}</td>
                  <td className="px-4 py-3">
                    {t.subscription_status ? (
                      <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${STATUS_COLORS[t.subscription_status] ?? STATUS_COLORS['incomplete']}`}>
                        {t.subscription_status}
                      </span>
                    ) : (
                      <span className="text-gray-400">—</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-gray-500">{new Date(t.created_at).toLocaleDateString()}</td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={7} className="px-4 py-12 text-center text-gray-400">
                    No tenants match “{search}”.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
